import { useEffect, useState } from "react";
import {
  getEmailProvider,
  sendTestEmail,
  setEmailProvider,
  type EmailProviderName,
  type EmailProviderStatus,
} from "../../api/emailLogsApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import RichTextEdit from "../../editors/RichTextEdit";
import { toast } from "react-toastify";

const DEFAULT_SUBJECT = "Test email from Labwick";

const EmailTestPanel = () => {
  const [to, setTo] = useState("");
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [html, setHtml] = useState("<p>This is a test email. You can use <strong>rich text</strong> here.</p>");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<{ messageId?: string; service?: string } | null>(null);
  const [providerStatus, setProviderStatus] = useState<EmailProviderStatus | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<EmailProviderName>("mailjet");
  const [loadingProvider, setLoadingProvider] = useState(true);
  const [savingProvider, setSavingProvider] = useState(false);

  const loadProviderStatus = async () => {
    setLoadingProvider(true);
    try {
      const status = await getEmailProvider();
      setProviderStatus(status);
      if (status.activeProvider === "mailjet" || status.activeProvider === "resend") {
        setSelectedProvider(status.activeProvider);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to load email provider settings.";
      toast.error(message);
    } finally {
      setLoadingProvider(false);
    }
  };

  useEffect(() => {
    void loadProviderStatus();
  }, []);

  const handleProviderSave = async () => {
    if (!selectedProvider) {
      return;
    }
    setSavingProvider(true);
    try {
      const result = await setEmailProvider(selectedProvider);
      setProviderStatus(result);
      toast.success(result.message || `Active provider set to ${selectedProvider}.`);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to update email provider.";
      toast.error(message);
    } finally {
      setSavingProvider(false);
    }
  };

  const handleSend = async () => {
    const trimmedTo = to.trim();
    if (!trimmedTo) {
      toast.error("Enter at least one email address (comma-separated).");
      return;
    }
    if (!html.trim()) {
      toast.error("Enter email body content.");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const result = await sendTestEmail({
        to: trimmedTo,
        subject: subject.trim() || DEFAULT_SUBJECT,
        html: html.trim()
      });
      setLastResult({ messageId: result.messageId, service: result.service });
      toast.success(result.message || "Test email sent.");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to send test email.";
      toast.error(message);
      setLastResult(null);
    } finally {
      setSending(false);
    }
  };

  const availableProviders = providerStatus?.availableProviders ?? [];
  const activeProvider = providerStatus?.activeProvider;
  const providerSourceLabel =
    providerStatus?.providerSource === "runtime" ? "runtime override" : "EMAIL_SERVICE env default";

  return (
    <div className="space-y-6 p-4 max-w-4xl">
      <Card>
        <CardHeader>
          <CardTitle>Email provider</CardTitle>
          <p className="text-sm text-gray-600">
            Switch between Mailjet and Resend without redeploy. Both providers must be configured in backend env.
            SMTP and SES remain env-only.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Active provider:</span>
            <Badge variant="default">{loadingProvider ? "Loading..." : activeProvider ?? "unknown"}</Badge>
            {!loadingProvider && providerStatus ? (
              <span className="text-xs text-gray-500">({providerSourceLabel})</span>
            ) : null}
          </div>
          <div className="grid gap-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="email-provider-select">Switch provider</Label>
              <Select
                value={selectedProvider}
                onValueChange={(value) => setSelectedProvider(value as EmailProviderName)}
                disabled={loadingProvider || savingProvider}
              >
                <SelectTrigger id="email-provider-select">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mailjet" disabled={!availableProviders.includes("mailjet")}>
                    Mailjet{!availableProviders.includes("mailjet") ? " (not configured)" : ""}
                  </SelectItem>
                  <SelectItem value="resend" disabled={!availableProviders.includes("resend")}>
                    Resend{!availableProviders.includes("resend") ? " (not configured)" : ""}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              variant="secondary"
              onClick={() => void handleProviderSave()}
              disabled={
                loadingProvider ||
                savingProvider ||
                selectedProvider === activeProvider ||
                !availableProviders.includes(selectedProvider)
              }
            >
              {savingProvider ? "Saving..." : "Apply provider switch"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Send test email</CardTitle>
          <p className="text-sm text-gray-600">
            Sends using the active runtime provider (Mailjet or Resend). Comma-separated recipients and rich text body below.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email-test-to">Recipients (comma-separated)</Label>
            <Input
              id="email-test-to"
              placeholder="one@example.com, two@example.com"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={sending}
              className="font-mono text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-test-subject">Subject</Label>
            <Input
              id="email-test-subject"
              placeholder={DEFAULT_SUBJECT}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email-test-body">Body (rich text)</Label>
            <div className="border rounded-md overflow-hidden min-h-[200px]">
              <RichTextEdit
                name="body"
                value={html}
                onChange={({ value }) => setHtml(value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button
              onClick={() => void handleSend()}
              disabled={sending}
            >
              {sending ? "Sending…" : "Send test email"}
            </Button>
            {lastResult && (
              <span className="text-sm text-gray-600">
                Sent via {lastResult.service ?? "email"}
                {lastResult.messageId && ` (${lastResult.messageId})`}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailTestPanel;
